import { Component, OnInit, Optional } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Catalog } from '@nousmedis/cc-types';
import { User } from '@nousmedis/guard-types/frontend';
import { catalogFormEditFields, catalogFormEditBasicFields, catalogFormEditImportFields, catalogFormEditExportFields, catalogFormEditExtraFields } from 'app/modules/catalog-cloud/schemas/catalog-form-wizard/catalog-form-edit.schema';
import { CatalogService } from 'app/modules/catalog-cloud/services/catalog.service';
import { AuthModel } from '@nousmedis/nm-guard-auth';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-catalog-sync-differences',
  templateUrl: './catalog-form-edit.component.html',
  styleUrls: ['./catalog-form-edit.component.scss'],
})
export class CatalogFormEditComponent implements OnInit {
  form = new FormGroup({});
  model: any = { extraFields: [] };
  fields: FormlyFieldConfig[] = [];
  basicFields: FormlyFieldConfig[] = [];
  importFields: FormlyFieldConfig[] = [];
  exportFields: FormlyFieldConfig[] = [];
  extraFields: FormlyFieldConfig[] = [];

  user: User;
  saveButton = false;
  loadingReSync = false;

  constructor(
    private catalogService: CatalogService,
    private auth: AuthModel,
    private messageService: MessageService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    @Optional() private dialogConfig?: DynamicDialogConfig,
    @Optional() private dialogRef?: DynamicDialogRef,
  ) {
    this.user = this.auth.getCurrentUser();
  }

  ngOnInit(): void {
    this.fields = catalogFormEditFields(this.translate);
    this.basicFields = catalogFormEditBasicFields(this.translate);
    this.importFields = catalogFormEditImportFields(this.translate);
    this.exportFields = catalogFormEditExportFields(this.translate);
    this.extraFields = catalogFormEditExtraFields(this.translate);

    const catalogId = this.dialogConfig?.data?.id ?? this.route.snapshot.paramMap.get('id');
    if (catalogId) {
      this.catalogService.getCatalog(catalogId).subscribe((data: Catalog) => {
        this.model = {
          id: catalogId,
          name: data.name,
          title: data.title,
          createdBy: this.user.id,
          updatedBy: this.user.id,
          importUrl: data.importCredentials?.url || '',
          privateImportKey: data.importCredentials?.token || '',
          exportUrl: data.exportCredentials?.url || '',
          privateExportKey: data.exportCredentials?.token || '',
          extraFields: (data.extra || [])
            .filter((e) => e !== null)
            .map((e) => ({ key: e.label || e.name, value: e.value })),
        };
      });
    }
  }

  private buildCatalogModel(): Catalog {
    const catalog = this.catalogService.initializeCatalog();
    catalog.id = this.model.id;
    catalog.name = this.model.name;
    catalog.title = this.model.title;
    catalog.createdBy = this.model.createdBy;
    catalog.updatedBy = this.model.updatedBy;
    catalog.importCredentials = {
      url: this.model.importUrl || '',
      token: this.model.privateImportKey || '',
    };
    catalog.exportCredentials = {
      url: this.model.exportUrl || '',
      token: this.model.privateExportKey || '',
    };
    catalog.extra = (this.model.extraFields || []).map(
      (ef: { key: string; value: string }) => ({
        name: ef.key.replace(/ /g, '_').toLowerCase(),
        label: ef.key,
        value: ef.value,
      })
    ) as any;
    return catalog;
  }

  updateForm(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor rellene el formulario correctamente.',
      });
      return;
    }

    this.catalogService.save(this.buildCatalogModel()).subscribe((response) => {
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Catálogo actualizado con éxito',
      });
      if (this.dialogRef) {
        this.dialogRef.close(response);
      } else {
        this.router.navigate(['/catalog/list']);
      }
    });
  }

  testImportUrl(): void {
    const importUrl = this.model.importUrl;
    const privateImportKey = this.model.privateImportKey;

    if (!importUrl) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'La URL de importación está vacía',
      });
      return;
    }

    this.loadingReSync = true;
    this.saveButton = true;

    this.catalogService
      .checkImport({ importUrl, privateImportKey })
      .then((result) => {
        if (result.success && result.importCheck) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'La URL de importación es válida.',
          });
          this.saveButton = false;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'La URL de importación no es válida o no se pudo conectar.',
          });
        }
      })
      .catch((error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Ocurrió un error al verificar la URL de importación.',
        });
        console.error(error);
      })
      .finally(() => {
        this.loadingReSync = false;
      });
  }

  testExportUrl(): void {
    const exportUrl = this.model.exportUrl;
    const privateExportKey = this.model.privateExportKey;

    if (!exportUrl) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'La URL de exportación está vacía',
      });
      return;
    }

    this.loadingReSync = true;
    this.saveButton = true;

    this.catalogService
      .checkExport({ exportUrl, privateExportKey })
      .then((result) => {
        if (result.success && result.exportCheck) {
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'La URL de exportación es válida.',
          });
          this.saveButton = false;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'La URL de exportación no es válida o no se pudo conectar.',
          });
        }
      })
      .catch((error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Ocurrió un error al verificar la URL de exportación.',
        });
        console.error(error);
      })
      .finally(() => {
        this.loadingReSync = false;
      });
  }
}
